// Pure Dashboard maths (architecture sprint-04 §6.1). Takes today/tz/instants as arguments; never
// reads a clock or the DOM.
import { instantToLocal, monthRange, SECONDS_PER_DAY, weekRange, type IanaZone, type LocalDate } from '@shared/calendar';
import { NO_PROJECT_LABEL } from '@shared/palette';
import type { EntryListItem } from '@/types/entries';
import type { Project } from '@/types/projects';
import type { DailyTotalRow } from '@/types/reports';

export const MAX_TIMELINE_LANES = 3;
export const RECENT_ENTRY_COUNT = 7;

export interface DashboardTotals {
  today: number;
  week: number;
  month: number;
}

function inRange(date: LocalDate, start: LocalDate, end: LocalDate): boolean {
  return date >= start && date <= end;
}

/** Saved entries only (the running timer is never added; approved decision 2). */
export function dashboardTotals(rows: DailyTotalRow[], today: LocalDate): DashboardTotals {
  const week = weekRange(today);
  const month = monthRange(today);
  const totals: DashboardTotals = { today: 0, week: 0, month: 0 };
  for (const r of rows) {
    if (r.date === today) totals.today += r.seconds;
    if (inRange(r.date, week.start, week.end)) totals.week += r.seconds;
    if (inRange(r.date, month.start, month.end)) totals.month += r.seconds;
  }
  return totals;
}

/** Newest first: date desc, startSecond desc, id desc; the first `count`. */
export function recentEntries(items: EntryListItem[], count: number = RECENT_ENTRY_COUNT): EntryListItem[] {
  return [...items]
    .sort((a, b) => (a.date !== b.date ? (a.date < b.date ? 1 : -1) : b.startSecond - a.startSecond || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0)))
    .slice(0, count);
}

export interface ProjectTotal {
  /** projectId, or 'none'. Grouping is by id, never by name (INV #15). */
  key: string;
  label: string;
  color: string | null;
  archived: boolean;
  seconds: number;
}

/** Seconds per project inside [range.start, range.end], sorted by seconds desc. */
export function totalsByProject(rows: DailyTotalRow[], range: { start: LocalDate; end: LocalDate }, projects: Project[]): ProjectTotal[] {
  const byId = new Map(projects.map((p) => [p.id, p]));
  const sums = new Map<string, ProjectTotal>();
  for (const r of rows) {
    if (!inRange(r.date, range.start, range.end) || r.seconds <= 0) continue;
    const key = r.projectId ?? 'none';
    let item = sums.get(key);
    if (!item) {
      const p = r.projectId ? byId.get(r.projectId) : undefined;
      item = {
        key,
        label: p?.name ?? NO_PROJECT_LABEL,
        color: p?.color ?? null,
        archived: p?.status === 'archived',
        seconds: 0,
      };
      sums.set(key, item);
    }
    item.seconds += r.seconds;
  }
  return [...sums.values()].sort((a, b) => b.seconds - a.seconds || a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }));
}

export function weekByProject(rows: DailyTotalRow[], week: { start: LocalDate; end: LocalDate }, projects: Project[]): ProjectTotal[] {
  return totalsByProject(rows, week, projects);
}

export interface TimelineBlock {
  key: string;
  start: number;
  end: number;
  /** The entry runs past 24:00 (drawn with a right-edge chevron). */
  continues: boolean;
  running: boolean;
  paused: boolean;
  entry: EntryListItem | null;
  project: Project | null;
  description: string | null;
}

export interface RunningInput {
  segmentStartedAt: string;
  /** pausedAt when paused, else the server-projected now (ms). */
  endMs: number;
  paused: boolean;
  project: Project | null;
  description: string | null;
}

function runningBlock(running: RunningInput, today: LocalDate, tz: IanaZone): TimelineBlock | null {
  const startLocal = instantToLocal(new Date(running.segmentStartedAt), tz);
  const endLocal = instantToLocal(new Date(running.endMs), tz);
  if (startLocal.date > today || endLocal.date < today) return null;
  const start = startLocal.date < today ? 0 : startLocal.second;
  const end = endLocal.date > today ? SECONDS_PER_DAY : endLocal.second;
  return {
    key: 'running',
    start,
    end: Math.max(start, end),
    continues: false,
    running: true,
    paused: running.paused,
    entry: null,
    project: running.project,
    description: running.description,
  };
}

/**
 * Today's entries (clipped at 24:00 with `continues`) plus the running timer (clipped at 00:00),
 * greedily packed into at most 3 lanes; a 4th overlap goes on top of lane 3 (A-6).
 */
export function layoutTimeline(entries: EntryListItem[], running: RunningInput | null, today: LocalDate, tz: IanaZone): { lanes: TimelineBlock[][] } {
  const blocks: TimelineBlock[] = entries
    .filter((e) => e.date === today)
    .map((e) => {
      const rawEnd = e.startSecond + e.durationSeconds;
      return {
        key: e.id,
        start: e.startSecond,
        end: Math.min(rawEnd, SECONDS_PER_DAY),
        continues: rawEnd > SECONDS_PER_DAY,
        running: false,
        paused: false,
        entry: e,
        project: e.project,
        description: e.description,
      };
    });
  const live = running ? runningBlock(running, today, tz) : null;
  if (live) blocks.push(live);
  blocks.sort((a, b) => a.start - b.start || a.end - b.end);

  const lanes: TimelineBlock[][] = [];
  for (const block of blocks) {
    const free = lanes.find((lane) => (lane[lane.length - 1]?.end ?? 0) <= block.start);
    if (free) free.push(block);
    else if (lanes.length < MAX_TIMELINE_LANES) lanes.push([block]);
    else lanes[MAX_TIMELINE_LANES - 1]?.push(block);
  }
  return { lanes };
}
